export type HttpExchangeFormattedOutput = {
  requestPayload: string | undefined;
  responsePayload: string | undefined;
  error: string | undefined;
};

export function tryParseJsonObject(
  output: string,
): Record<string, unknown> | null {
  const trimmed = output.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }

    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function extractStructuredProbeOutput(
  output: string,
): Record<string, unknown> | null {
  const direct = tryParseJsonObject(output);
  if (direct) {
    return direct;
  }

  const trimmed = output.trim();
  const jsonStart = trimmed.indexOf('{');
  if (jsonStart === -1) {
    return null;
  }

  return tryParseJsonObject(trimmed.slice(jsonStart));
}

export function isPlainTextHttpToolError(output: string): boolean {
  const trimmed = output.trim();
  return (
    trimmed.startsWith('Failed to fetch OpenAPI spec:') ||
    trimmed.startsWith('OpenAPI fetch returned') ||
    trimmed.startsWith('OpenAPI fetch returned invalid JSON') ||
    trimmed.startsWith('SSRF validation failed:') ||
    trimmed.startsWith('Failed to execute HTTP request') ||
    trimmed.startsWith('HTTP request failed')
  );
}

function buildRawRequestLine(
  source: Record<string, unknown>,
): string | undefined {
  const url = typeof source.url === 'string' ? source.url : undefined;
  if (!url) return undefined;

  const method = typeof source.method === 'string' ? source.method : 'GET';
  const headers = source.headers as Record<string, string> | undefined;
  const body = typeof source.body === 'string' ? source.body : undefined;
  const headerLines = headers
    ? Object.entries(headers)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n')
    : '';
  return `${method} ${url} HTTP/1.1${headerLines ? `\n${headerLines}` : ''}${body ? `\n\n${body}` : ''}`;
}

function formatStructuredHttpResponse(
  parsed: Record<string, unknown>,
): Pick<HttpExchangeFormattedOutput, 'responsePayload' | 'error'> {
  const res = parsed.response as Record<string, unknown> | undefined;
  if (res) {
    const statusCode = res.statusCode ?? res.status ?? '';
    const statusText = typeof res.statusText === 'string' ? res.statusText : '';
    const result = typeof res.result === 'string' ? res.result : '';
    const resError =
      typeof res.error === 'string' && res.error ? res.error : undefined;
    const responsePayload =
      result || statusCode
        ? `HTTP/1.1 ${statusCode} ${statusText}\n${result}`
        : undefined;

    return { responsePayload, error: resError };
  }

  if (
    typeof parsed.statusCode === 'number' ||
    typeof parsed.status === 'number'
  ) {
    const statusCode = parsed.statusCode ?? parsed.status ?? '';
    const statusText =
      typeof parsed.statusText === 'string' ? parsed.statusText : '';
    const body =
      typeof parsed.body === 'string'
        ? parsed.body
        : typeof parsed.result === 'string'
          ? parsed.result
          : '';
    return {
      responsePayload: `HTTP/1.1 ${statusCode} ${statusText}${body ? `\n\n${body}` : ''}`,
      error: undefined,
    };
  }

  return {
    responsePayload: JSON.stringify(parsed, null, 2),
    error: undefined,
  };
}

export function formatHttpExchangePayloads(
  args: Record<string, unknown> | undefined,
  output: string,
): HttpExchangeFormattedOutput {
  let requestPayload: string | undefined;
  let responsePayload: string | undefined;
  let error: string | undefined;

  if (args) {
    requestPayload = buildRawRequestLine(args);
  }

  const parsed = extractStructuredProbeOutput(output);
  if (parsed) {
    if (
      !requestPayload &&
      typeof parsed.request === 'object' &&
      parsed.request !== null
    ) {
      requestPayload = buildRawRequestLine(
        parsed.request as Record<string, unknown>,
      );
    }

    const formatted = formatStructuredHttpResponse(parsed);
    responsePayload = formatted.responsePayload;
    error = formatted.error;
    return { requestPayload, responsePayload, error };
  }

  const trimmedOutput = output.trim();
  if (!trimmedOutput) {
    return { requestPayload, responsePayload, error };
  }

  if (isPlainTextHttpToolError(trimmedOutput)) {
    return { requestPayload, responsePayload, error: trimmedOutput };
  }

  return { requestPayload, responsePayload: trimmedOutput, error };
}
